import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { SITE, NAV_LINKS } from "../config/site";

const testId = (label) =>
  "nav-" + label.toLowerCase().replace(/\s+/g, "-").replace(/à/g, "a");

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-md border-b border-slate-200/80 shadow-[0_8px_30px_-18px_rgba(8,42,89,0.25)]"
          : "bg-white/60 backdrop-blur-sm border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-[76px] flex items-center justify-between gap-6">
        <Link to="/" data-testid="nav-logo" className="flex items-center shrink-0">
          <img
            src={SITE.logo}
            alt="Centro Studi Forma Futuro"
            className="h-9 sm:h-11 w-auto"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-7" aria-label="Navigazione principale">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={testId(l.label)}
              className={({ isActive }) =>
                `relative text-[13.5px] font-semibold tracking-wide transition-colors duration-200 after:absolute after:-bottom-1.5 after:left-0 after:h-[2px] after:rounded-full after:bg-azure after:transition-all after:duration-300 ${
                  isActive
                    ? "text-azure after:w-full"
                    : "text-navy/70 hover:text-navy after:w-0 hover:after:w-full"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/contatti"
            data-testid="nav-cta-button"
            className="hidden md:inline-flex items-center rounded-full bg-azure px-5 py-2.5 text-[13.5px] font-bold text-white shadow-[0_10px_24px_-10px_rgba(36,85,138,0.7)] transition-all duration-300 hover:bg-[#1C446E] hover:-translate-y-0.5"
          >
            Richiedi informazioni
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-full border border-slate-200 text-navy bg-white/80"
            aria-label="Apri menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-md border-b border-slate-200"
          >
            <div className="px-6 py-5 flex flex-col gap-1">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -14 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 + i * 0.04 }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`nav-mobile-${testId(l.label)}`}
                    className={({ isActive }) =>
                      `block py-2.5 text-base font-semibold ${
                        isActive ? "text-azure" : "text-navy/80"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <Link
                to="/contatti"
                data-testid="nav-mobile-cta"
                className="mt-3 inline-flex justify-center rounded-full bg-azure px-5 py-3 text-sm font-bold text-white"
              >
                Richiedi informazioni
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
